<template>
  <w-sys-page
    v-if="isConfigLoaded"
    ref="wSysPage"
    :key="pageInstanceKey"
    mode="runtime"
    :project-config="projectConfig"
    :config="config"
    :page-list="pageList"
  />
</template>

<script>
import { mapState } from 'vuex';
import { pageScrollToTop } from '@utils/browser';
import { usePreviewConsumer } from '@composables/pagePreview/use-preview';
import { useRouter } from '@/router/useRouter';
import moduleRegistry from '@utils/dependencyLoader/module-registry';
import { vueDeepSet } from '@tencent/ui-core/lib/utils';
import { getCurrentInstance, onBeforeMount, onUnmounted } from '@vue/composition-api';
import { usePreviewerSnackbar } from '@/composables/pagePreview/usePreviewerSnackbar';

export default {
  name: 'RuntimePage',
  provide() {
    return {
      routeContext: this.routeContext,
    };
  },
  async beforeRouteLeave(to, from, next) {
    if (this.beforeUnloadHandler) {
      return await this.beforeUnloadHandler(to, from, next);
    }
    return next();
  },
  // 在当前路由改变，但是该组件被复用时调用
  beforeRouteUpdate(to, from, next) {
    // 判断是否为 Hash 变化
    const isHashChange = (to.hash || from.hash) && from.name === to.name && to?.fullPath?.replace(to?.hash, '') === from?.fullPath?.replace(from?.hash, '');

    // 当组件为非 keep-alive 时, 刷新对应的 UcRenderer 实例
    // block_reload_uc_renderer 为页面 props 双向绑定时对路径修改时会带上的参数
    if (!to.meta?.isKeepAlive && to?.params?.block_reload_uc_renderer !== true) {
      if (isHashChange) return next();
      this.reloadUcRenderer();
    }

    // 非 Hash 变化重置滚动条位置
    if (!isHashChange) pageScrollToTop();

    return next();
  },
  props: {
    projectId: {
      type: String,
      default: '',
    },
    pageId: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { route } = useRouter();

    const previewConsumer = usePreviewConsumer(route.value.query.previewKey || '');
    const {
      isPreviewMode: getIsPreviewMode,
      addPreviewListener,
      removePreviewListener,
      fetchPreviewData,
    } = previewConsumer;

    const isPreviewMode = getIsPreviewMode(); // 避免 history.replace 变化
    if (isPreviewMode) {
      const self = getCurrentInstance();
      if (+route.value.query.iframe !== 1) usePreviewerSnackbar(previewConsumer);
      onBeforeMount(() => addPreviewListener(self.proxy));
      onUnmounted(() => removePreviewListener());
    }

    return {
      isPreviewMode,
      addPreviewListener,
      removePreviewListener,
      fetchPreviewData,
    };
  },
  data() {
    return {
      pageInstanceKey: Date.now().toString(36),
      config: null,
      projectConfig: null,
      pageList: [],
      // 路由组件的上下文
      beforeUnloadHandler: null,

      routeContext: {
        setBeforeUnloadHandler: this.setBeforeRouteLeave,
        getBeforeUnloadHandler: this.getBeforeRouteLeave,
      },

      loadingConfigKeys: [],
    };
  },
  computed: {
    ...mapState({
      env: state => state.app.env,
      branch: state => state.app.branch,
      project: state => state.runtime.project,
    }),
    isConfigLoaded() {
      return !!(this.config && this.projectConfig);
    },
  },
  watch: {
    pageId: {
      handler() {
        this.config = null;
        this.init();
        this.removeBeforeUnloadHandler();
      },
      immediate: true,
    },
  },
  async activated() {
    // 页面被激活时重新获取页面数据
    if (!this.config) {
      const config = await this.getConfig();
      if (config) this.config = config;
    }
  },
  destroyed() {
    this.removeBeforeUnloadHandler();
  },
  methods: {
    // 刷新页面实例
    reloadPageInstance() {
      this.pageInstanceKey = Date.now().toString(36);
    },
    // 刷新渲染器实例
    reloadUcRenderer() {
      this.$refs.wSysPage?.reloadUcRenderer('*');
    },
    // 刷新渲染器实例
    reloadUcRendererById(id) {
      this.$refs.wSysPage?.reloadUcRenderer(id);
    },
    async updateDependency(payload) {
      await moduleRegistry.load(payload);
      this.reloadUcRenderer();
    },
    async init() {
      const { projectId, env } = this;

      // 合并获取应用, 页面列表, 当前页面的数据
      const [appInfo, pageList, page] = await Promise.all([
        this.$store.dispatch('runtime/getProject', { projectId, env }),
        this.$store.dispatch('runtime/getBasicPageList', { projectId, env }),
        this.getConfig(),
      ]);

      if (env === 'dev') {
        const skinId = this.$route.query.wujiskin;
        if (skinId) {
          const styles = await this.$store.dispatch('getSkinStyles', { skinId });
          vueDeepSet(appInfo, 'uiInfo.componentStyles', styles);
        }
      }

      this.projectConfig = appInfo;
      this.pageList = pageList;
      if (page) this.config = page;

      if (this.isPreviewMode) {
        this.fetchPreviewData();
      }
    },
    setConfig(config) {
      this.config = config;
    },
    async getConfig() {
      const { projectId, pageId, env, branch } = this;

      const key = `${projectId}:${pageId}:${env}:${branch}`;
      if (this.loadingConfigKeys.includes(key)) return;
      this.loadingConfigKeys.push(key);

      let config = null;
      try {
        config = await this.$store.dispatch('runtime/getPageDetail', { projectId, pageId, env, branch });
      } finally {
        this.loadingConfigKeys = this.loadingConfigKeys.filter(k => k !== key);
        return config;
      }
    },
    setProjectConfig(config) {
      this.projectConfig = config;
    },
    removeBeforeUnloadHandler() {
      this.beforeUnloadHandler = null;
    },
    setBeforeRouteLeave(handler) {
      this.beforeUnloadHandler = handler;
    },
    getBeforeRouteLeave() {
      return this.beforeUnloadHandler;
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
